import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicApi, Style, SkillLevel, Instructor, Move, MoveType } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VideoModal from '../components/VideoModal';
import VideoThumbnail from '../components/VideoThumbnail';
import { SkeletonGrid } from '../components/Skeleton';
import '../components/PlanRequired.css';

// MoveCard component defined OUTSIDE LibraryPage to prevent re-creation on every render
interface MoveCardProps {
  move: Move;
  isVariation?: boolean;
  hasLibraryAccess: boolean;
  onSelectMove: (move: Move) => void;
  onLoginRedirect: () => void;
}

const MoveCard = memo<MoveCardProps>(({
  move,
  isVariation = false,
  hasLibraryAccess,
  onSelectMove,
  onLoginRedirect,
}) => {
  // Get the default video and thumbnail
  const defaultVideo = move.videos?.find(video => video.isDefault) || move.videos?.[0];
  const thumbnailUrl = defaultVideo?.thumbnail || move.thumbnail;
  const videoUrl = defaultVideo?.videoUrl || move.videoUrl;
  const navigate = useNavigate();
  // Signed in but this move needs a plan: send them to the plans, not the player
  const locked = hasLibraryAccess && !!move.locked;

  return (
    <div className={`video-card ${isVariation ? 'variation-card' : ''}`}>
      {/* Video Thumbnail */}
      <div className="locked-video">
        {move.accessLevel === 'members' && <span className="locked-video-badge">Members</span>}
        <VideoThumbnail
          videoUrl={videoUrl || ''}
          thumbnailUrl={thumbnailUrl}
          alt={`${move.title} thumbnail`}
          onClick={() => (locked ? navigate('/join') : hasLibraryAccess ? onSelectMove(move) : onLoginRedirect())}
        />
      </div>

      <div className="move-header">
        <div className="move-title-container">
          {isVariation && <span className="variation-indent" aria-hidden="true">&darr; </span>}
          <span className="move-type-icon" aria-hidden="true">
            {move.isBaseMove ? '\uD83D\uDCCB' : '\uD83D\uDD04'}
          </span>
          <h3>
            <Link to={`/library/moves/${move.id}`} className="move-title-link">
              {move.title}
            </Link>
          </h3>
          {move.variationDescription && (
            <span className="variation-badge">{move.variationDescription}</span>
          )}
          <span className="move-type-label">
            {move.isBaseMove ? 'Base move' : 'Variation'}
          </span>
        </div>
      </div>
      <p className="description">{move.description}</p>
      <div className="move-meta">
        <span className="style">{move.style?.name}</span>
        <span className="level">{move.skillLevel?.name}</span>
        <span className="instructor">by {move.instructor?.name}</span>
      </div>
      {(defaultVideo?.duration || move.duration) && (
        <div className="duration">
          Duration: {Math.floor((defaultVideo?.duration || move.duration!) / 60)}:{((defaultVideo?.duration || move.duration!) % 60).toString().padStart(2, '0')}
        </div>
      )}
      {!isVariation && move.variations && move.variations.length > 0 && (
        <Link
          to={`/library/moves/${move.id}?section=variations`}
          className="variations-toggle toggle-link"
        >
          {move.variations.length} variation{move.variations.length !== 1 ? 's' : ''}
        </Link>
      )}
      <Link
        to={`/library/moves/${move.id}?section=demonstrations`}
        className="demonstrations-toggle toggle-link"
      >
        {move._count?.demonstrations != null
          ? `${move._count.demonstrations} demonstration${move._count.demonstrations !== 1 ? 's' : ''}`
          : 'Show demonstrations'}
      </Link>
    </div>
  );
});

MoveCard.displayName = 'MoveCard';

const LibraryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasLibraryAccess = !!user?.role;
  const [styles, setStyles] = useState<Style[]>([]);
  const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [moveTypes, setMoveTypes] = useState<MoveType[]>([]);
  const [moves, setMoves] = useState<Move[]>([]);
  const [filteredMoves, setFilteredMoves] = useState<Move[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedStyleId, setSelectedStyleId] = useState<string>('');
  const [selectedSkillLevelId, setSelectedSkillLevelId] = useState<string>('');
  const [selectedInstructorId, setSelectedInstructorId] = useState<string>('');
  const [selectedMoveTypeId, setSelectedMoveTypeId] = useState<string>('');

  // Video modal state
  const [selectedMove, setSelectedMove] = useState<Move | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stylesData, skillLevelsData, instructorsData, moveTypesData, movesData] = await Promise.all([
          publicApi.styles.getAll(),
          publicApi.skillLevels.getAll(),
          publicApi.instructors.getAll(),
          publicApi.moveTypes.getAll(),
          publicApi.moves.getAll({ includeVariations: true })
        ]);

        setStyles(stylesData);
        setSkillLevels(skillLevelsData);
        setInstructors(instructorsData);
        setMoveTypes(moveTypesData);
        setMoves(movesData);
        setFilteredMoves(movesData);
      } catch (err) {
        setError('Failed to load data');
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    const applyFilters = async () => {
      try {
        const filters: any = {};
        if (selectedStyleId) filters.styleId = selectedStyleId;
        if (selectedSkillLevelId) filters.skillLevelId = selectedSkillLevelId;
        if (selectedInstructorId) filters.instructorId = selectedInstructorId;
        if (selectedMoveTypeId) filters.moveTypeId = selectedMoveTypeId;

        const filteredData = await publicApi.moves.getAll({ ...filters, includeVariations: true });
        setFilteredMoves(filteredData);
      } catch (err) {
        console.error('Error filtering moves:', err);
        // If filtering fails, show all moves
        setFilteredMoves(moves);
      }
    };

    applyFilters();
  }, [selectedStyleId, selectedSkillLevelId, selectedInstructorId, selectedMoveTypeId, moves]);

  const clearFilters = () => {
    setSelectedStyleId('');
    setSelectedSkillLevelId('');
    setSelectedInstructorId('');
    setSelectedMoveTypeId('');
  };

  // Memoized callbacks to prevent unnecessary re-renders
  const handleLoginRedirect = useCallback(() => {
    navigate('/login');
  }, [navigate, user]);

  const handleSelectMove = useCallback((move: Move) => {
    setSelectedMove(move);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedMove(null);
  }, []);

  if (loading) {
    return (
      <div className="library-page">
        <SkeletonGrid />
      </div>
    );
  }

  if (error) {
    return (
      <div className="library-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="library-page">
      <h1>Moves</h1>
      <p>A collection of moves and variations, each typically a single 8-count — the foundation that patterns are built upon.</p>

      {/* Filters Section */}
      <div className="filters-section">
        <h2>Filter Lessons</h2>
        <div className="filters">
          <div className="filter-group">
            <label htmlFor="style-filter">Dance Style:</label>
            <select
              id="style-filter"
              value={selectedStyleId}
              onChange={(e) => setSelectedStyleId(e.target.value)}
            >
              <option value="">All Styles</option>
              {styles.map((style) => (
                <option key={style.id} value={style.id}>
                  {style.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="skill-filter">Skill Level:</label>
            <select
              id="skill-filter"
              value={selectedSkillLevelId}
              onChange={(e) => setSelectedSkillLevelId(e.target.value)}
            >
              <option value="">All Levels</option>
              {skillLevels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="instructor-filter">Instructor:</label>
            <select
              id="instructor-filter"
              value={selectedInstructorId}
              onChange={(e) => setSelectedInstructorId(e.target.value)}
            >
              <option value="">All Instructors</option>
              {instructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="move-type-filter">Move Type:</label>
            <select
              id="move-type-filter"
              value={selectedMoveTypeId}
              onChange={(e) => setSelectedMoveTypeId(e.target.value)}
            >
              <option value="">All Move Types</option>
              {moveTypes.map((moveType) => (
                <option key={moveType.id} value={moveType.id}>
                  {moveType.name}
                </option>
              ))}
            </select>
          </div>

          <button className="btn btn-secondary btn-small clear-filters" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>

        <div className="results-count">
          Showing {filteredMoves.filter(move => move.isBaseMove).length + filteredMoves.filter(move => !move.isBaseMove && !filteredMoves.some(baseMove => baseMove.isBaseMove && baseMove.id === move.parentMoveId)).length} lesson{(filteredMoves.filter(move => move.isBaseMove).length + filteredMoves.filter(move => !move.isBaseMove && !filteredMoves.some(baseMove => baseMove.isBaseMove && baseMove.id === move.parentMoveId)).length) !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Moves Grid */}
      <div className="moves-section">
        <h2>Dance Lessons</h2>
        {filteredMoves.length > 0 ? (
          <div className="video-grid">
            {/* Show base moves */}
            {filteredMoves.filter(move => move.isBaseMove).map((move) => (
              <div key={move.id} className="move-container">
                <MoveCard
                  move={move}
                  hasLibraryAccess={hasLibraryAccess}
                  onSelectMove={handleSelectMove}
                  onLoginRedirect={handleLoginRedirect}
                />
              </div>
            ))}

            {/* Show standalone variations (variations whose parent moves don't match current filter) */}
            {filteredMoves
              .filter(move => !move.isBaseMove && !filteredMoves.some(baseMove => baseMove.isBaseMove && baseMove.id === move.parentMoveId))
              .map((variation) => (
                <div key={variation.id} className="move-container">
                  <MoveCard
                    move={variation}
                    isVariation={true}
                    hasLibraryAccess={hasLibraryAccess}
                    onSelectMove={handleSelectMove}
                    onLoginRedirect={handleLoginRedirect}
                  />
                </div>
              ))}
          </div>
        ) : (
          <div className="empty-state-block">
            <div className="empty-state-icon" aria-hidden="true">💃</div>
            <h3>No moves match these filters</h3>
            <p>Try different criteria, or clear the filters to see the full syllabus.</p>
            <button className="btn btn-primary" onClick={clearFilters}>Clear filters</button>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {selectedMove && (
        <VideoModal
          move={selectedMove}
          onClose={handleCloseModal}
        />
      )}

    </div>
  );
};

export default LibraryPage;
